import { useEffect, useRef } from "react"
import { cn } from "@/lib/utils"
import { slideAlternatingBlocks, slideInFrom, sectionHeaderClasses } from "@/lib/section-slide"
import gsap from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"

gsap.registerPlugin(ScrollTrigger)

const expertise = [
  "Full-Chip RTL-to-GDSII Implementation",
  "Logic Synthesis",
  "Floorplanning & Power Planning",
  "Placement & Optimization",
  "Clock Tree Synthesis (CTS)",
  "Timing Closure",
  "Static Timing Analysis (STA)",
  "Constraint Development & Validation",
  "Physical Verification (DRC/LVS)",
  "EM/IR Analysis & Reliability Closure",
  "Low Power Implementation (UPF/CPF)",
  "ECO & Design Closure",
  "Physical Design Methodology and flow Development",
]

const markets = [
  "AI",
  "High-Performance Computing",
  "Networking",
  "Automotive",
  "Consumer Electronics",
  "Wireless",
  "IoT",
]

const advantages = [
  {
    title: "Industry Leadership",
    description:
      "Decades of experience delivering complex semiconductor programs for global technology companies.",
  },
  {
    title: "Proven Silicon Delivery",
    description:
      "Experience across advanced process nodes and numerous successful ASIC and SoC tape-outs.",
  },
  {
    title: "Specialized Expertise",
    description:
      "Focused on Physical Design, signoff and silicon implementation for unmatched technical depth.",
  },
  {
    title: "Scalable Engineering Teams",
    description:
      "Dedicated teams tailored to each technology, schedule and project requirement.",
  },
  {
    title: "Customer-Centric Engagement",
    description:
      "Flexible models spanning critical milestones, managed projects and long-term dedicated ODCs.",
  },
  {
    title: "Execution Excellence",
    description:
      "Predictable schedules, engineering quality, transparent communication and successful tape-outs.",
  },
]

export function AboutSection() {
  const sectionRef = useRef<HTMLElement>(null)
  const headerRef = useRef<HTMLDivElement>(null)
  const storyRef = useRef<HTMLDivElement>(null)
  const expertiseRef = useRef<HTMLDivElement>(null)
  const odcRef = useRef<HTMLDivElement>(null)
  const advantagesRef = useRef<HTMLDivElement>(null)
  const purposeRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!sectionRef.current) return

    const ctx = gsap.context(() => {
      slideInFrom(headerRef.current, "right")
      slideInFrom(storyRef.current, "left", { delay: 0.1 })
      slideAlternatingBlocks(expertiseRef.current, "[data-expertise-item]", "left")
      slideInFrom(odcRef.current, "right")
      slideAlternatingBlocks(advantagesRef.current, "[data-advantage-card]", "left")
      slideAlternatingBlocks(purposeRef.current, "[data-purpose-card]", "left")
    }, sectionRef)

    return () => ctx.revert()
  }, [])

  return (
    <section
      ref={sectionRef}
      id="about"
      className="relative overflow-hidden border-t border-border/30 py-32 pl-6 pr-6 md:pl-28 md:pr-12"
    >
      <div
        className="pointer-events-none absolute right-0 top-0 h-80 w-80 bg-[radial-gradient(circle_at_top_right,oklch(0.7_0.2_45_/_0.1),transparent_68%)]"
        aria-hidden="true"
      />

      <div
        ref={headerRef}
        className={cn("relative mb-16 max-w-5xl", sectionHeaderClasses("right"))}
      >
        <span className="mb-4 block font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
          About AEXIOM
        </span>
        <h2 className="font-[var(--font-bebas)] text-5xl leading-[1.02] tracking-tight uppercase md:text-7xl lg:text-8xl">
          Engineering The Future
          <br />
          <span className="text-accent">Of Silicon</span>
        </h2>
      </div>

      <div
        ref={storyRef}
        className="relative grid border border-border/30 bg-card/60 lg:grid-cols-[0.72fr_1.28fr]"
      >
        <div className="flex flex-col justify-between border-b border-border/30 p-8 lg:border-b-0 lg:border-r lg:p-12">
          <div>
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-accent">
              Silicon Implementation Partner
            </span>
            <p className="mt-6 font-[var(--font-bebas)] text-3xl leading-tight uppercase text-foreground md:text-4xl">
              Ambitious ideas.
              <br />
              Production with Foundry.
            </p>
          </div>

          <div className="mt-10 flex flex-wrap gap-2">
            {markets.map((market) => (
              <span
                key={market}
                className="border border-border/50 px-3 py-1.5 font-mono text-[9px] uppercase tracking-wider text-muted-foreground"
              >
                {market}
              </span>
            ))}
          </div>
        </div>

        <div className="space-y-6 p-8 lg:p-12">
          <p className="max-w-3xl font-mono text-sm leading-7 text-foreground/90 md:text-base">
            At AEXIOM Semiconductors, we help semiconductor companies transform ambitious ideas into
            production-ready silicon. We are a specialist semiconductor engineering company delivering
            end-to-end Physical Design Implementation services for complex ASICs and SoCs across advanced
            technology nodes.
          </p>
          <p className="max-w-3xl font-mono text-sm leading-7 text-muted-foreground md:text-base">
            Founded by industry veterans with decades of experience and successful tape-outs across AI,
            High-Performance Computing, Networking, Automotive, Consumer Electronics, Wireless and IoT,
            AEXIOM combines deep technical expertise with an execution-driven approach to consistently
            deliver first-time-right silicon.
          </p>
          <p className="max-w-3xl font-mono text-sm leading-7 text-muted-foreground md:text-base">
            We build dedicated Offshore Development Centers that integrate with customer engineering
            organizations, providing scalable, high-quality teams that operate as a natural extension of
            each customer&apos;s design group.
          </p>
        </div>
      </div>

      <div className="mt-24">
        <div className="mb-10 max-w-3xl">
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-accent">
            Our Expertise
          </span>
          <h3 className="mt-4 font-[var(--font-bebas)] text-4xl leading-tight uppercase md:text-6xl">
            Complete Physical Design
            <br />
            Implementation
          </h3>
          <p className="mt-5 font-mono text-sm leading-7 text-muted-foreground">
            From a critical milestone to complete full-chip implementation, our engineers deliver
            predictable execution without compromising quality.
          </p>
        </div>

        <div
          ref={expertiseRef}
          className="grid gap-px border border-border/30 bg-border/30 md:grid-cols-2"
        >
          {expertise.map((item, index) => (
            <div
              key={item}
              data-expertise-item
              className="group flex min-h-20 items-center gap-5 bg-card/90 px-6 py-5 transition-colors duration-300 hover:bg-accent/[0.06] md:px-8"
            >
              <span className="shrink-0 font-mono text-[10px] tabular-nums text-accent">
                {String(index + 1).padStart(2, "0")}
              </span>
              <span className="font-mono text-xs leading-relaxed text-foreground/90 md:text-sm">
                {item}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div
        ref={odcRef}
        className="relative mt-24 overflow-hidden border border-border/30 bg-card/70 p-8 md:p-12 lg:p-16"
      >
        <div className="absolute inset-x-0 top-0 h-1 bg-accent" aria-hidden="true" />
        <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16">
          <div>
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-accent">
              ODC Model
            </span>
            <h3 className="mt-5 font-[var(--font-bebas)] text-4xl leading-tight uppercase md:text-6xl">
              Built For
              <br />
              Long-Term Success
            </h3>
          </div>

          <div className="space-y-6">
            <p className="font-mono text-sm leading-7 text-foreground/90 md:text-base">
              AEXIOM&apos;s Offshore Development Center model enables semiconductor companies to rapidly
              expand engineering capacity while maintaining complete control over project execution, IP
              security and engineering quality.
            </p>
            <p className="font-mono text-sm leading-7 text-muted-foreground md:text-base">
              Our dedicated teams align with your methodologies, design flows, tools and delivery
              processes—combining the flexibility and responsiveness of an in-house engineering team with
              the efficiency of an offshore model.
            </p>
          </div>
        </div>
      </div>

      <div className="mt-24" id="why-aexiom">
        <div className="mb-10">
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-accent">
            Why AEXIOM
          </span>
          <h3 className="mt-4 font-[var(--font-bebas)] text-4xl uppercase md:text-6xl">
            Technical Depth. Execution Focus.
          </h3>
        </div>

        <div
          ref={advantagesRef}
          className="grid gap-px border border-border/30 bg-border/30 sm:grid-cols-2 lg:grid-cols-3"
        >
          {advantages.map((advantage, index) => (
            <article
              key={advantage.title}
              data-advantage-card
              className="group relative min-h-64 bg-card/90 p-8 transition-colors duration-300 hover:bg-foreground/[0.03]"
            >
              <span className="font-mono text-[10px] tabular-nums text-accent">
                {String(index + 1).padStart(2, "0")}
              </span>
              <h4 className="mt-10 font-[var(--font-bebas)] text-2xl uppercase transition-colors duration-300 group-hover:text-accent md:text-3xl">
                {advantage.title}
              </h4>
              <div className="my-5 h-px w-10 bg-accent/60 transition-all duration-500 group-hover:w-full" />
              <p className="font-mono text-xs leading-6 text-muted-foreground md:text-sm">
                {advantage.description}
              </p>
            </article>
          ))}
        </div>
      </div>

      <div
        ref={purposeRef}
        className="mt-24 grid gap-px border border-border/30 bg-border/30 md:grid-cols-2"
      >
        <article data-purpose-card className="bg-card/90 p-8 md:p-12">
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-accent">
            Our Mission
          </span>
          <p className="mt-6 font-[var(--font-bebas)] text-3xl leading-tight uppercase md:text-4xl">
            Accelerate innovation. Reduce project risk.
          </p>
          <p className="mt-6 font-mono text-sm leading-7 text-muted-foreground">
            To become the most trusted semiconductor engineering partner by delivering world-class
            silicon implementation services that enable customers to bring breakthrough products to
            market faster.
          </p>
        </article>

        <article data-purpose-card className="bg-card/90 p-8 md:p-12">
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-accent">
            Our Vision
          </span>
          <p className="mt-6 font-[var(--font-bebas)] text-3xl leading-tight uppercase md:text-4xl">
            Globally recognized. Technically exceptional.
          </p>
          <p className="mt-6 font-mono text-sm leading-7 text-muted-foreground">
            To build a globally recognized semiconductor engineering company renowned for technical
            excellence, innovation, integrity and lasting customer partnerships.
          </p>
        </article>
      </div>
    </section>
  )
}
